/**
 * Device-local notification preferences.
 *
 * Sound and OS-notification behaviour is a property of the device you're
 * holding (phone in your pocket vs. wall dashboard), not of the grow account,
 * so these live in localStorage — NOT Firestore. A tiny external-store
 * subscribe API keeps the NotificationContext, the Settings card, and the
 * AlertsBanner quick-mute in sync without prop drilling.
 */

import { useSyncExternalStore } from 'react';

export type SoundLevel = 'critical-only' | 'warning+critical';

export interface DevicePrefs {
  /** Master switch for the alert beep on this device. */
  soundEnabled: boolean;
  /** Which severities trigger the beep. */
  soundLevel: SoundLevel;
  /** OS/browser notifications (shown when the tab is hidden). */
  browserNotificationsEnabled: boolean;
  /** In-app toast popups (info alerts like "Emergency Resolved"). */
  toastsEnabled: boolean;
  /** Sticky warning/critical Alerts banner at the top of the app. */
  alertsBannerEnabled: boolean;
}

const STORAGE_KEY = 'ai-controller.devicePrefs.v1';

const DEFAULT_PREFS: DevicePrefs = {
  soundEnabled: true,
  soundLevel: 'warning+critical',
  browserNotificationsEnabled: true,
  toastsEnabled: true,
  alertsBannerEnabled: true,
};

function load(): DevicePrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<DevicePrefs>;
    return {
      soundEnabled: typeof parsed.soundEnabled === 'boolean' ? parsed.soundEnabled : DEFAULT_PREFS.soundEnabled,
      soundLevel: parsed.soundLevel === 'critical-only' ? 'critical-only' : 'warning+critical',
      browserNotificationsEnabled: typeof parsed.browserNotificationsEnabled === 'boolean'
        ? parsed.browserNotificationsEnabled
        : DEFAULT_PREFS.browserNotificationsEnabled,
      toastsEnabled: typeof parsed.toastsEnabled === 'boolean'
        ? parsed.toastsEnabled
        : DEFAULT_PREFS.toastsEnabled,
      alertsBannerEnabled: typeof parsed.alertsBannerEnabled === 'boolean'
        ? parsed.alertsBannerEnabled
        : DEFAULT_PREFS.alertsBannerEnabled,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

let current: DevicePrefs = load();
const listeners = new Set<() => void>();

function emit(): void {
  for (const cb of listeners) cb();
}

export function getDevicePrefs(): DevicePrefs {
  return current;
}

export function setDevicePrefs(patch: Partial<DevicePrefs>): void {
  current = { ...current, ...patch };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch {
    // Storage full / private mode — keep the in-memory value for this session.
  }
  emit();
}

export function subscribeDevicePrefs(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

// Keep multiple open tabs on the same device in sync.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      current = load();
      emit();
    }
  });
}

/** React hook view over the store. */
export function useDevicePrefs(): DevicePrefs {
  return useSyncExternalStore(subscribeDevicePrefs, getDevicePrefs, getDevicePrefs);
}

/** Should the alert beep play for this severity on this device? */
export function shouldPlaySound(severity: 'info' | 'warning' | 'critical'): boolean {
  if (!current.soundEnabled) return false;
  if (severity === 'critical') return true;
  return severity === 'warning' && current.soundLevel === 'warning+critical';
}

/** Should an OS notification be shown for this severity on this device? */
export function shouldShowBrowserNotification(severity: 'info' | 'warning' | 'critical'): boolean {
  return current.browserNotificationsEnabled && severity !== 'info';
}

/** Should an in-app toast popup be shown on this device? */
export function shouldShowToast(): boolean {
  return current.toastsEnabled;
}

/** Should the sticky Alerts banner be shown on this device? */
export function shouldShowAlertsBanner(): boolean {
  return current.alertsBannerEnabled;
}
